/**
 * Shared Dropdown Helpers
 *
 * Centralised helpers for detecting, searching, filtering, and selecting
 * dropdown options.  Used by both OnboardingSDK and ActionExecutor /
 * HybridTargetResolver so that every call-site shares one code-path.
 *
 * The helpers are framework-agnostic and cover Angular NZ-Select / CDK
 * overlays, React-Select, Ant Design, MUI, Headless-UI, Radix, and generic
 * ARIA combobox / listbox patterns.
 */

import { logger } from './logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Visibility predicate – callers pass HybridTargetResolver.isVisible */
export type IsVisibleFn = (el: HTMLElement) => boolean;

// ---------------------------------------------------------------------------
// Constants – selector lists shared across helpers
// ---------------------------------------------------------------------------

/** Selectors that indicate an open dropdown/listbox is on the page. */
const OPEN_DROPDOWN_SELECTORS = [
  // Angular NZ Select: overlay pane with dropdown content
  '.cdk-overlay-pane nz-option-container',
  '.cdk-overlay-pane .ant-select-dropdown',
  // Angular CDK overlay with listbox
  '.cdk-overlay-pane [role="listbox"]',
  // Generic open dropdowns
  '[role="listbox"]:not([aria-hidden="true"])',
  // React Select open menu
  '[class*="menu"][class*="--is-open"]',
  '[class*="MenuList"]',
  // Ant Design dropdown visible
  '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
  // Expanded combobox
  'input[role="combobox"][aria-expanded="true"]',
];

/** Selectors for containers that hold dropdown options. */
const MENU_SELECTORS = [
  '[role="listbox"]',
  '[role="menu"]',
  '[class*="-menu"]:not([aria-hidden="true"])',
  '[class*="listbox"]',
  '[class*="options-container"]',
  '.ant-select-dropdown',
  'nz-option-container',
  '.cdk-overlay-pane',
  '.cdk-virtual-scroll-content-wrapper',
];

/** Compound selector for individual option elements inside a menu. */
const OPTION_SELECTOR = [
  '[role="option"]',
  '[class*="option"]:not([class*="singleValue"]):not([class*="placeholder"])',
  'nz-option-item',
  '.ant-select-item-option',
  '.ant-select-item-option-content',
  '.ant-select-item',
].join(', ');

/** Selectors used to locate a search <input> inside/near a dropdown. */
const SEARCH_INPUT_SELECTORS = [
  'input.ant-select-selection-search-input',
  'input[role="combobox"]',
  'input[type="search"]',
  'input[class*="search"]',
  'input[class*="Search"]',
  'input[aria-autocomplete="list"]',
];

// ---------------------------------------------------------------------------
// 1. isDropdownCurrentlyOpen
// ---------------------------------------------------------------------------

/**
 * Check whether *any* dropdown / listbox is currently open on the page.
 *
 * Detects Angular NZ-Select, CDK overlays, React-Select, Ant Design, and
 * generic `[role="listbox"]` / `[role="combobox"]` patterns.
 */
export function isDropdownCurrentlyOpen(isVisible: IsVisibleFn): boolean {
  for (const selector of OPEN_DROPDOWN_SELECTORS) {
    try {
      const el = document.querySelector(selector) as HTMLElement;
      if (el && isVisible(el)) {
        const rect = el.getBoundingClientRect();
        if (rect.height > 20 && rect.width > 20) {
          logger.log('[DropdownHelpers] Dropdown detected as open via:', selector);
          return true;
        }
      }
    } catch {
      continue;
    }
  }

  return false;
}

// ---------------------------------------------------------------------------
// 2. findDropdownSearchInput
// ---------------------------------------------------------------------------

/**
 * Find the search `<input>` within or associated with a dropdown trigger.
 *
 * Works with Angular NZ-Select, React-Select, Ant Design, and generic
 * combobox patterns.
 */
export function findDropdownSearchInput(
  trigger: HTMLElement,
  isVisible: IsVisibleFn,
): HTMLInputElement | null {
  // Strategy 1: Trigger itself is a search input
  if (
    trigger instanceof HTMLInputElement &&
    (trigger.getAttribute('role') === 'combobox' ||
      trigger.classList.contains('ant-select-selection-search-input') ||
      trigger.type === 'search')
  ) {
    logger.log('[DropdownHelpers] Trigger itself is search input');
    return trigger;
  }

  // Strategy 2: Find search input within the trigger's parent component
  const selectComponent = trigger.closest(
    'nz-select, [class*="select-container"], [class*="Select"], [class*="combobox"]',
  );
  const searchContext = selectComponent || trigger.parentElement;

  if (searchContext) {
    for (const sel of SEARCH_INPUT_SELECTORS) {
      const input = searchContext.querySelector(sel) as HTMLInputElement;
      if (input) {
        logger.log('[DropdownHelpers] Found search input via:', sel);
        return input;
      }
    }
  }

  // Strategy 3: Look in CDK overlay pane (Angular puts dropdowns in overlay)
  const overlayPanes = document.querySelectorAll('.cdk-overlay-pane');
  for (const pane of overlayPanes) {
    if (!isVisible(pane as HTMLElement)) continue;
    for (const sel of SEARCH_INPUT_SELECTORS) {
      const input = pane.querySelector(sel) as HTMLInputElement;
      if (input) {
        logger.log('[DropdownHelpers] Found search input in CDK overlay via:', sel);
        return input;
      }
    }
  }

  // Strategy 4: Check the currently focused element
  const activeEl = document.activeElement;
  if (
    activeEl instanceof HTMLInputElement &&
    (activeEl.getAttribute('role') === 'combobox' ||
      activeEl.classList.contains('ant-select-selection-search-input'))
  ) {
    logger.log('[DropdownHelpers] Found search input as active element');
    return activeEl;
  }

  logger.log('[DropdownHelpers] No search input found for dropdown');
  return null;
}

// ---------------------------------------------------------------------------
// 3. getSearchTermsForOption
// ---------------------------------------------------------------------------

/**
 * Generate progressively more specific search terms for a given option label.
 *
 * Example: `"Brandon Mora (brandon@example.com)"` → `["Brandon", "Brandon Mora"]`
 */
export function getSearchTermsForOption(optionText: string): string[] {
  const terms: string[] = [];

  // Strip parenthetical info like "(email@...)"
  const nameMatch = optionText.match(/^([^(]+)/);
  const name = nameMatch ? nameMatch[1].trim() : optionText;

  // First word (first name) — usually enough to filter significantly
  const firstWord = name.split(/\s+/)[0];
  if (firstWord && firstWord.length >= 2) {
    terms.push(firstWord);
  }

  // Full name (without email/parenthetical)
  if (name !== firstWord) {
    terms.push(name);
  }

  // Fallback: use the original text
  if (terms.length === 0) {
    terms.push(optionText);
  }

  return terms;
}

// ---------------------------------------------------------------------------
// 4. findVisibleDropdownOption
// ---------------------------------------------------------------------------

/**
 * Find a visible dropdown option whose text matches `optionText`.
 *
 * Uses **strict** matching: an exact text match is preferred, and partial
 * matches are only accepted when they cover >70 % of the candidate text
 * (prevents false positives like selecting "United States" when searching for
 * "United").
 */
export function findVisibleDropdownOption(
  optionText: string,
  isVisible: IsVisibleFn,
): HTMLElement | null {
  const normalizedText = optionText.toLowerCase().trim();

  let exactMatch: HTMLElement | null = null;
  let partialMatch: HTMLElement | null = null;

  for (const menuSelector of MENU_SELECTORS) {
    let menus: NodeListOf<Element>;
    try {
      menus = document.querySelectorAll(menuSelector);
    } catch {
      continue;
    }

    for (const menu of menus) {
      const menuEl = menu as HTMLElement;
      if (!isVisible(menuEl)) continue;
      const rect = menuEl.getBoundingClientRect();
      if (rect.height < 20) continue;

      const options = menu.querySelectorAll(OPTION_SELECTOR);
      for (const option of options) {
        const optionEl = option as HTMLElement;

        // Skip display values
        const className = optionEl.className || '';
        if (
          className.toLowerCase().includes('singlevalue') ||
          className.toLowerCase().includes('placeholder')
        ) {
          continue;
        }

        // Skip virtual-scroll ghost items
        const optRect = optionEl.getBoundingClientRect();
        if (optRect.height === 0 || optRect.width === 0) continue;

        const text = optionEl.textContent?.trim().toLowerCase() || '';

        // Prefer exact match
        if (text === normalizedText) {
          logger.log(`[DropdownHelpers] Found EXACT match for option: "${optionText}"`);
          exactMatch = optionEl;
          break;
        }

        // Accept partial match only if it covers >70 % of candidate text
        if (!partialMatch && text.includes(normalizedText)) {
          const matchRatio = normalizedText.length / text.length;
          if (matchRatio > 0.7) {
            logger.log(
              `[DropdownHelpers] Found partial match (${(matchRatio * 100).toFixed(0)}%): "${text.slice(0, 50)}"`,
            );
            partialMatch = optionEl;
          }
        }
      }

      if (exactMatch) break;
    }

    if (exactMatch) break;
  }

  if (exactMatch) return exactMatch;
  if (partialMatch) {
    logger.warn(
      `[DropdownHelpers] Using partial match for "${optionText}" — verify correctness`,
    );
    return partialMatch;
  }

  logger.log(`[DropdownHelpers] No option found for: "${optionText}"`);
  return null;
}

// ---------------------------------------------------------------------------
// 5. typeToFilterDropdown
// ---------------------------------------------------------------------------

/**
 * Type text into a dropdown search input to filter options, then locate the
 * target option.  Uses progressive typing: tries the first word first, then
 * adds more words if the option isn't found.
 */
export async function typeToFilterDropdown(
  searchInput: HTMLInputElement,
  optionText: string,
  isVisible: IsVisibleFn,
): Promise<HTMLElement | null> {
  const searchTerms = getSearchTermsForOption(optionText);

  for (const searchTerm of searchTerms) {
    logger.log(`[DropdownHelpers] Typing search term: "${searchTerm}"`);

    // Focus the input
    searchInput.focus();
    await new Promise(r => setTimeout(r, 50));

    // Clear existing text
    searchInput.value = '';
    searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    searchInput.dispatchEvent(new Event('change', { bubbles: true }));
    await new Promise(r => setTimeout(r, 100));

    // Type character-by-character for framework change-detection
    for (let i = 0; i < searchTerm.length; i++) {
      const char = searchTerm[i];
      searchInput.value = searchTerm.substring(0, i + 1);

      searchInput.dispatchEvent(new KeyboardEvent('keydown', { key: char, bubbles: true }));
      searchInput.dispatchEvent(new KeyboardEvent('keypress', { key: char, bubbles: true }));
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      searchInput.dispatchEvent(new KeyboardEvent('keyup', { key: char, bubbles: true }));

      await new Promise(r => setTimeout(r, 30));
    }

    searchInput.dispatchEvent(new Event('change', { bubbles: true }));

    // Wait for dropdown to filter
    await new Promise(r => setTimeout(r, 400));

    let optionElement = findVisibleDropdownOption(optionText, isVisible);
    if (optionElement) return optionElement;

    // Virtual scroll might need extra time
    await new Promise(r => setTimeout(r, 300));
    optionElement = findVisibleDropdownOption(optionText, isVisible);
    if (optionElement) return optionElement;
  }

  // Clean up: clear the search input so the dropdown shows all options again
  searchInput.value = '';
  searchInput.dispatchEvent(new Event('input', { bubbles: true }));
  searchInput.dispatchEvent(new Event('change', { bubbles: true }));
  await new Promise(r => setTimeout(r, 200));

  return null;
}
